import { LitElement, html, css, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { applyRole, resolveGap, resolvePad } from '../utils.js';

/**
 * uwc-box — Generic block container
 *
 * The foundation of every layout. A neutral `display:block` wrapper that can
 * carry padding, gap (for slotted flex/grid children), an explicit display
 * mode, and a semantic ARIA landmark via `as`.
 *
 * Philosophy: reach for `uwc-box` whenever you need a container whose sole
 * job is to hold something — add padding, set a semantic role, or change
 * display mode. It has no layout opinions of its own; pair it with
 * `uwc-stack` or `uwc-grid` when you need layout.
 *
 * @attr {string}  as       - Semantic landmark: header|footer|nav|main|aside|section|article
 * @attr {string}  pad      - Padding token (none xs sm md lg xl 2xl) or any CSS value
 * @attr {string}  gap      - Gap between children when display is flex/grid
 * @attr {string}  display  - CSS display value. Default: block
 *
 * @cssprop --uwc-box-pad     - Override padding directly
 * @cssprop --uwc-box-gap     - Override gap directly
 * @cssprop --uwc-box-display - Override display directly
 */
export class UwcBox extends LitElement {
  static override styles = css`
    :host {
      display:    var(--uwc-box-display, var(--_display, block));
      width:      100%;
      min-width:  0;
      padding:    var(--uwc-box-pad,     var(--_pad,     0));
      gap:        var(--uwc-box-gap,     var(--_gap,     0));
      box-sizing: border-box;
    }
  `;

  /** Maps to an ARIA landmark role on the host element. */
  @property({ type: String, reflect: true }) as      = '';
  /** Padding: design token or any CSS value. */
  @property({ type: String })                pad     = '';
  /** Gap between direct children. */
  @property({ type: String })                gap     = '';
  /** CSS display value. Default: block. */
  @property({ type: String })                display = '';

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('as'))      applyRole(this, this.as);
    if (changed.has('pad'))     this.style.setProperty('--_pad',     resolvePad(this.pad));
    if (changed.has('gap'))     this.style.setProperty('--_gap',     resolveGap(this.gap));
    if (changed.has('display')) this.style.setProperty('--_display', this.display || 'block');
  }

  override render() { return html`<slot></slot>`; }
}
